/**
 * Represent-style PDP media lightbox:
 * full-viewport overlay + vertical scroll through product images.
 */
(function () {
  'use strict';

  var OPEN_CLASS = 'is-open';
  var BODY_CLASS = 'pml-noscroll';
  var SCROLL_LOCK_Y = 0;
  // Match Flickity dragThreshold so swipe-to-change-slide never opens lightbox
  var DRAG_THRESHOLD = 10;

  function visibleSlides(lightbox) {
    return Array.from(lightbox.querySelectorAll('.pml__slide:not(.is-filtered)'));
  }

  function syncFilteredSlides(productMedia, lightbox) {
    var mediaItems = productMedia.querySelectorAll('.product__media-item[data-media-id]');
    var filteredIds = new Set();

    mediaItems.forEach(function (item) {
      if (item.classList.contains('is-filtered')) {
        filteredIds.add(String(item.getAttribute('data-media-id')));
      }
    });

    lightbox.querySelectorAll('.pml__slide[data-media-id]').forEach(function (slide) {
      var id = String(slide.getAttribute('data-media-id'));
      var mediaType = slide.getAttribute('data-media-type') || 'image';
      var shouldHide = filteredIds.has(id) || mediaType !== 'image';
      slide.classList.toggle('is-filtered', shouldHide);
    });
  }

  function getSelectedMediaId(productMedia) {
    var selected =
      productMedia.querySelector('.product__media-item.is-selected:not(.is-filtered)') ||
      productMedia.querySelector('.product__media-item.is-initial-selected:not(.is-filtered)') ||
      productMedia.querySelector('.product__media-item:not(.is-filtered)');

    return selected ? selected.getAttribute('data-media-id') : null;
  }

  function lockScroll() {
    SCROLL_LOCK_Y = window.scrollY || window.pageYOffset || 0;
    document.documentElement.classList.add(BODY_CLASS);
    document.body.style.top = '-' + SCROLL_LOCK_Y + 'px';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
  }

  function unlockScroll() {
    document.documentElement.classList.remove(BODY_CLASS);
    document.body.style.top = '';
    document.body.style.position = '';
    document.body.style.width = '';
    window.scrollTo(0, SCROLL_LOCK_Y);
  }

  function ensureHqImage(img) {
    if (!img) {
      return;
    }
    var hq = img.getAttribute('data-pml-hq');
    if (!hq) {
      return;
    }
    // Force the full-res CDN URL (strip any smaller srcset the browser may have cached)
    if (img.getAttribute('src') !== hq) {
      img.setAttribute('src', hq);
    }
    img.removeAttribute('srcset');
    img.loading = 'eager';
    img.setAttribute('fetchpriority', 'high');
  }

  function preloadAdjacentHq(slides, activeIndex) {
    [activeIndex - 1, activeIndex, activeIndex + 1].forEach(function (index) {
      if (index < 0 || index >= slides.length) {
        return;
      }
      ensureHqImage(slides[index].querySelector('img.pml__image, img'));
    });
  }

  function openLightbox(productMedia, lightbox, mediaId) {
    syncFilteredSlides(productMedia, lightbox);

    var slides = visibleSlides(lightbox);
    if (!slides.length) {
      return;
    }

    var targetId = mediaId || getSelectedMediaId(productMedia);
    var target =
      (targetId && lightbox.querySelector('.pml__slide[data-media-id="' + targetId + '"]:not(.is-filtered)')) ||
      slides[0];
    var activeIndex = Math.max(0, slides.indexOf(target));

    // Upgrade opened (+neighbors) to HQ before paint
    preloadAdjacentHq(slides, activeIndex);
    slides.forEach(function (slide) {
      ensureHqImage(slide.querySelector('img.pml__image, img'));
    });

    lightbox.classList.add(OPEN_CLASS);
    lightbox.setAttribute('aria-hidden', 'false');
    lightbox.removeAttribute('hidden');
    lockScroll();

    requestAnimationFrame(function () {
      if (target) {
        target.scrollIntoView({ block: 'start' });
      }
      var closeBtn = lightbox.querySelector('[data-pml-close]');
      if (closeBtn) {
        closeBtn.focus({ preventScroll: true });
      }
    });
  }

  function closeLightbox(lightbox) {
    if (!lightbox.classList.contains(OPEN_CLASS)) {
      return;
    }

    lightbox.classList.remove(OPEN_CLASS);
    lightbox.setAttribute('aria-hidden', 'true');
    lightbox.setAttribute('hidden', '');
    unlockScroll();
  }

  function bindProductMedia(productMedia) {
    if (productMedia.hasAttribute('data-pml-bound')) {
      return;
    }

    var lightbox = productMedia.querySelector('product-media-lightbox');
    if (!lightbox) {
      return;
    }

    productMedia.setAttribute('data-pml-bound', '');

    var wrapper = productMedia.querySelector('.product__media-list-wrapper');
    if (wrapper) {
      wrapper.setAttribute('data-pml-enabled', '');
    }

    var zoomButton = productMedia.querySelector('.product__zoom-button');
    if (zoomButton) {
      zoomButton.removeAttribute('is');
      zoomButton.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        openLightbox(productMedia, lightbox, getSelectedMediaId(productMedia));
      });
    }

    var carousel = productMedia.querySelector('flickity-carousel.product__media-list');
    if (carousel) {
      var pointer = { x: 0, y: 0, moved: false, active: false };

      carousel.addEventListener(
        'pointerdown',
        function (event) {
          if (event.isPrimary === false) {
            return;
          }
          pointer.x = event.clientX;
          pointer.y = event.clientY;
          pointer.moved = false;
          pointer.active = true;
        },
        true
      );

      carousel.addEventListener(
        'pointermove',
        function (event) {
          if (!pointer.active) {
            return;
          }
          if (
            Math.abs(event.clientX - pointer.x) > DRAG_THRESHOLD ||
            Math.abs(event.clientY - pointer.y) > DRAG_THRESHOLD
          ) {
            pointer.moved = true;
          }
        },
        true
      );

      carousel.addEventListener(
        'pointerup',
        function () {
          pointer.active = false;
        },
        true
      );

      carousel.addEventListener(
        'pointercancel',
        function () {
          pointer.active = false;
          pointer.moved = true;
        },
        true
      );

      // If Flickity starts a drag, never treat the following click as an open
      function bindFlickityDragGuard() {
        if (carousel.hasAttribute('data-pml-drag-guard') || !carousel.flickityInstance) {
          return;
        }
        if (typeof carousel.flickityInstance.then !== 'function') {
          return;
        }
        carousel.setAttribute('data-pml-drag-guard', '');
        carousel.flickityInstance.then(function (flickity) {
          if (!flickity || typeof flickity.on !== 'function') {
            return;
          }
          flickity.on('dragStart', function () {
            pointer.moved = true;
          });
        });
      }

      bindFlickityDragGuard();
      carousel.addEventListener('flickity:ready', bindFlickityDragGuard);

      carousel.addEventListener(
        'click',
        function (event) {
          // Drag / swipe to change slide — do not open lightbox
          if (pointer.moved) {
            pointer.moved = false;
            return;
          }

          var imageWrapper = event.target.closest('.product__media-image-wrapper');
          if (!imageWrapper) {
            return;
          }

          var item = imageWrapper.closest('.product__media-item');
          if (!item || item.classList.contains('is-filtered')) {
            return;
          }
          if (item.getAttribute('data-media-type') !== 'image') {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
          openLightbox(productMedia, lightbox, item.getAttribute('data-media-id'));
        },
        true
      );
    }

    lightbox.querySelectorAll('[data-pml-close]').forEach(function (btn) {
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        closeLightbox(lightbox);
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && lightbox.classList.contains(OPEN_CLASS)) {
        closeLightbox(lightbox);
      }
    });

    // Keep lightbox slides in sync when color filtering changes the main gallery
    var formId = productMedia.getAttribute('form-id');
    if (formId) {
      var form = document.getElementById(formId);
      if (form) {
        form.addEventListener('variant:changed', function () {
          requestAnimationFrame(function () {
            syncFilteredSlides(productMedia, lightbox);
          });
        });
      }
    }

    var observer = new MutationObserver(function () {
      syncFilteredSlides(productMedia, lightbox);
    });
    observer.observe(productMedia, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });

    syncFilteredSlides(productMedia, lightbox);
  }

  function init() {
    document.querySelectorAll('product-media').forEach(bindProductMedia);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target) return;
    event.target.querySelectorAll('product-media').forEach(bindProductMedia);
  });
})();
